/**
 * The player ball + its googly face.
 *
 *  - `image` is the arcade-physics body (the skinned ball). Collision uses this.
 *  - The face (two eye-whites, two pupils, a mouth) lives in a separate
 *    Container that we sync to the ball each step. Arcade physics ignores
 *    container transforms, so the body MUST stay a top-level object.
 *
 * Pupils idle by orbiting in a circle ("googly eyes") and smoothly lock onto
 * the nearest danger as it approaches (blend weighted by proximity).
 */
import Phaser from 'phaser';
import { PLAYER } from '../core';
import type { Skin } from './skins';
import { ensureBallTexture, ballTextureKey } from './textures';

const TWO_PI = Math.PI * 2;

export class Player {
  readonly image: Phaser.Physics.Arcade.Image;

  private readonly face: Phaser.GameObjects.Container;
  private readonly leftPupil: Phaser.GameObjects.Arc;
  private readonly rightPupil: Phaser.GameObjects.Arc;

  // Face geometry (local to the ball centre), derived from the radius.
  private readonly eyeDX: number;
  private readonly eyeDY: number;
  private readonly orbitR: number;

  private phase = 0;

  constructor(scene: Phaser.Scene, x: number, y: number, skin: Skin) {
    ensureBallTexture(scene, skin);

    this.image = scene.physics.add.image(x, y, ballTextureKey(skin.id));
    this.image.setCircle(PLAYER.radius);
    this.image.setCollideWorldBounds(true);
    (this.image.body as Phaser.Physics.Arcade.Body).setAllowGravity(false);
    this.image.setDepth(10);

    const r = PLAYER.radius;
    this.eyeDX = r * 0.5; // wider spacing between the eyes
    this.eyeDY = r * 0.12;
    const eyeR = r * 0.4;
    const pupilR = r * 0.19;
    this.orbitR = eyeR - pupilR - r * 0.04;

    // Eye-whites (static relative to the ball) + pupils (animated).
    const leftWhite = scene.add.circle(-this.eyeDX, -this.eyeDY, eyeR, skin.sclera);
    const rightWhite = scene.add.circle(this.eyeDX, -this.eyeDY, eyeR, skin.sclera);
    this.leftPupil = scene.add.circle(-this.eyeDX, -this.eyeDY, pupilR, skin.pupil);
    this.rightPupil = scene.add.circle(this.eyeDX, -this.eyeDY, pupilR, skin.pupil);

    // Open, worried "uh-oh" mouth (legacy player.png): a dark ellipse carved at the
    // top by a body-colour ellipse, so the visible dark part reads as an open lower
    // mouth rather than a flat oval.
    const mouth = scene.add.ellipse(0, r * 0.5, r * 0.78, r * 0.52, skin.mouth);
    const mouthCarve = scene.add.ellipse(0, r * 0.3, r * 0.86, r * 0.46, skin.body);

    this.face = scene.add.container(x, y, [
      mouth,
      mouthCarve,
      leftWhite,
      rightWhite,
      this.leftPupil,
      this.rightPupil,
    ]);
    this.face.setDepth(11);
  }

  get x(): number {
    return this.image.x;
  }
  get y(): number {
    return this.image.y;
  }

  setPosition(x: number, y: number): void {
    this.image.setPosition(x, y);
  }

  /**
   * Advance the face one fixed step. `danger` is the nearest obstacle (world
   * coords) within `lookRange`, or null when nothing is close.
   */
  update(dtSeconds: number, danger: { x: number; y: number } | null, lookRange: number): void {
    this.face.setPosition(this.image.x, this.image.y);
    this.phase = (this.phase + dtSeconds * PLAYER.eyeOrbitSpeed) % TWO_PI;

    // Each eye gets a different idle phase so they wander independently.
    this.aimPupil(this.leftPupil, -this.eyeDX, -this.eyeDY, this.phase, danger, lookRange);
    this.aimPupil(
      this.rightPupil,
      this.eyeDX,
      -this.eyeDY,
      this.phase + Math.PI * 0.5,
      danger,
      lookRange,
    );
  }

  private aimPupil(
    pupil: Phaser.GameObjects.Arc,
    cx: number,
    cy: number,
    phase: number,
    danger: { x: number; y: number } | null,
    lookRange: number,
  ): void {
    // Idle: orbit around the eye centre.
    let ox = Math.cos(phase) * this.orbitR;
    let oy = Math.sin(phase) * this.orbitR;

    if (danger) {
      // Eye centre in world space (face container is unrotated, so world = ball + local).
      const ex = this.image.x + cx;
      const ey = this.image.y + cy;
      const dx = danger.x - ex;
      const dy = danger.y - ey;
      const dist = Math.hypot(dx, dy);
      // Snappy lock-on: ramps to a near-full lock well before the danger is
      // right on top of us (sqrt curve + gain), instead of a gentle linear fade.
      const proximity = Phaser.Math.Clamp(1 - dist / lookRange, 0, 1);
      const track = Phaser.Math.Clamp(Math.sqrt(proximity) * 1.5, 0, 1);
      if (dist > 0.0001 && track > 0) {
        const tx = (dx / dist) * this.orbitR;
        const ty = (dy / dist) * this.orbitR;
        ox = ox * (1 - track) + tx * track;
        oy = oy * (1 - track) + ty * track;
      }
    }

    pupil.setPosition(cx + ox, cy + oy);
  }

  /** Game-over expression: tint the ball and freeze the pupils. */
  setDead(): void {
    this.image.setTint(0xff6b6b);
  }

  /** Hide the ball + face (e.g. the death explosion has replaced it). */
  hide(): void {
    this.image.setVisible(false);
    this.face.setVisible(false);
  }

  destroy(): void {
    this.face.destroy(true);
    this.image.destroy();
  }
}
